export const mv = {
  meta: { title: 'AI MV 生成', desc: '從一個想法生成歌詞、音樂和 MV。' },
  hero: {
    kicker: 'AI 音樂影片工具',
    title: '一句話生成歌詞、音樂和 MV',
    desc: '面向新手的引導式流程：描述歌曲，編輯歌詞，生成音樂，再上傳圖片製作 MV 草稿。',
    tags: ['一句話提示詞', '可編輯歌詞', '音樂預覽', '歌詞到 MV'],
    cardTitle: '可編輯副歌',
    cardText: '每一行歌詞都可以先確認，再進入音樂和 MV 生成。',
  },
  steps: { lyrics: '歌詞', music: '音樂', video: 'MV' },
  flow: {
    available: '可進入',
    current: '目前步驟',
    done: '已完成',
    locked: '待解鎖',
    stepOf: '第 {current} / {total} 步',
    lyrics: {
      title: '先創作歌詞',
      desc: '從一句話開始生成結構化歌詞，後續音樂和 MV 都會基於這版歌詞繼續創作。',
    },
    music: {
      title: '用確認後的歌詞生成音樂',
      desc: '根據歌詞繼續設定音樂風格、人聲、節奏和音樂提示詞。',
    },
    video: {
      title: '音樂完成後製作 MV',
      desc: '上傳圖片並描述畫面方向，把歌曲繼續生成可預覽的 MV 草稿。',
    },
  },
  summary: {
    lyricsTitle: '已確認歌詞',
    musicTitle: '已生成音樂',
    editLyrics: '編輯歌詞',
    editMusic: '編輯音樂',
  },
  generator: {
    lyrics: {
      idea: '描述你想做的歌',
      placeholder: '例如：寫一首關於夏天畢業旅行的治癒中文歌',
      genre: '音樂風格',
      mood: '心情',
      vocal: '人聲',
      language: '語言',
      length: '長度',
      settings: '歌詞設定',
      button: '生成歌詞',
    },
    music: {
      prompt: '描述你想要的音樂',
      placeholder: '例如：明亮流行風，副歌抓耳，吉他清爽，溫柔女聲',
      upload: '上傳音樂參考圖片',
      formats: '可選。上傳 1-5 張圖片，根據畫面氛圍生成音樂。',
      selected: '已選擇 {count} 張圖片',
      style: '音樂風格',
      mood: '情緒',
      vocal: '人聲',
      language: '語言',
      mode: '模式',
      duration: '時長',
      settings: '音樂設定',
      button: '生成音樂',
    },
    video: {
      prompt: '描述你想要的 MV',
      placeholder: '例如：夏日畢業旅行感，柔和轉場，溫暖懷舊色調',
      upload: '上傳 MV 圖片',
      formats: '支援 PNG、JPG、WebP，可上傳單圖或多圖。',
      selected: '已選擇 {count} 張圖片',
      ratio: '畫幅',
      resolution: '解析度',
      visual: '畫面風格',
      settings: 'MV 設定',
      button: '生成 MV',
    },
  },
  preview: {
    emptyLyricsTitle: '還沒有歌詞',
    emptyLyrics: '生成的歌詞會顯示在這裡。',
    emptyLyricsHint: '輸入歌曲想法後，可生成標準結構的可編輯歌詞',
    emptyMusicTitle: '還沒有音樂',
    emptyMusic: '歌詞準備好後，可以生成音樂。',
    emptyMusicHint: '支援音樂風格、人聲、節奏和自訂音樂提示詞',
    emptyMvTitle: '還沒有 MV',
    emptyMv: '上傳圖片後，可以生成最終 MV 草稿。',
    emptyMvHint: '支援單圖、多圖、畫幅比例和畫面風格控制',
    lyricsTitle: '可編輯歌詞',
    lyricsHint: '可以複製、編輯、插入或刪除歌詞行。',
    lyricsReady: '歌詞已準備好',
    lyricsNext: '確認這版歌詞後，再用它繼續生成音樂。',
    musicTitle: '音樂預覽',
    musicPlaceholder: '這裡會顯示音樂試聽。',
    musicReady: '音樂已生成',
    musicNext: '確認這段音樂後，再用它繼續製作 MV。',
    mvTitle: 'MV 預覽',
    mvPlaceholder: '這裡會顯示影片預覽。',
    sourceImages: '使用的圖片',
    lyricsBackdrop: '同步歌詞',
  },
  actions: {
    copy: '複製歌詞',
    copySuccess: '歌詞已複製',
    copyFailed: '複製失敗',
    edit: '編輯歌詞',
    save: '儲存歌詞',
    download: '下載',
    downloadFailed: '下載失敗',
    play: '播放',
    pause: '暫停',
    volume: '音量',
    audioLoading: '正在載入音訊...',
    audioLoadFailed: '音訊載入失敗',
    regenerate: '重新生成',
    regenerateWithCredits: '重新生成（{count} 積分）',
    generateWithCredits: '{label}（{count} 積分）',
    generatingWithCredits: '生成中 {progress}%（{count} 積分）',
    expand: '展開',
    collapse: '收起',
    selectSettings: '請選擇設定',
    nextMusic: '使用這版歌詞並繼續',
    nextMv: '使用這段音樂並繼續',
  },
  examples: {
    title: 'AI MV 示例：歌曲故事、紀念場景與品牌短片',
    sub: '看看一句想法如何被拆解成結構化歌詞、音樂方向和基於圖片的 MV 草稿。',
    items: [
      { badge: '旅行', title: '畢業旅行', text: '治癒歌詞、流行旋律和照片回憶 MV。' },
      { badge: '紀念', title: '週年紀念歌', text: '浪漫歌詞、溫柔人聲和電影感轉場。' },
      { badge: '品牌', title: '產品主題曲', text: '抓耳副歌和品牌視覺，適合短影片推廣。' },
    ],
  },
  howto: {
    title: '如何用 AI 建立完整音樂 MV',
    sub: '透過三個引導步驟完成創作：生成可編輯歌詞，確定音樂風格，再上傳圖片製作精緻 MV 草稿。',
    items: [
      { title: '生成歌詞', text: '輸入一句話，選擇心情和語言，得到結構化歌詞。' },
      { title: '生成音樂', text: '選擇音樂風格、人聲和節奏，生成後預設使用。' },
      { title: '製作 MV', text: '上傳單圖或多圖，設定尺寸和風格，生成 MV 草稿。' },
    ],
  },
  ask: {
    title: 'AI MV 生成器 - 常見問題',
    sub: '了解歌詞編輯、音樂生成、圖片上傳、重新生成和 MV 下載相關問題。',
    items: [
      { q: '新手能用嗎？', a: '可以。一句話就能開始，每一步都有明確的下一步按鈕。' },
      { q: '歌詞可以改嗎？', a: '可以複製、編輯、插入和刪除歌詞行。' },
      { q: '不滿意可以重來嗎？', a: '可以，每一步都支援重新生成，同時保留整體流程。' },
    ],
  },
};
